<h2 class="flex items-center gap-2 px-6 pt-4 text-2xl">
  <mat-icon>{{ isEditMode ? 'edit' : 'person_add' }}</mat-icon>
  {{ isEditMode ? 'Edit User' : 'Invite User' }}
</h2>

<mat-dialog-content class="max-w-md">
  @if (isSubmitting$ | async) {
    <div
      class="absolute left-0 top-0 z-10 flex h-full w-full items-center justify-center bg-white/70"
    >
      <mat-progress-spinner
        mode="indeterminate"
        color="primary"
        diameter="50"
      ></mat-progress-spinner>
    </div>
  }

  <form
    [formGroup]="userForm"
    (ngSubmit)="onSave()"
    class="flex flex-col gap-2"
  >
    <div class="flex w-full gap-2">
      <div class="min-w-0 flex-1">
        <mat-form-field appearance="fill" class="w-full">
          <mat-label>First Name</mat-label>
          <input
            matInput
            formControlName="firstName"
            placeholder="Enter first name"
          />
        </mat-form-field>

        @if (firstName?.invalid && firstName?.touched) {
          <mat-error
            class="-mt-2 rounded-b-md bg-red-100 p-1 text-xs text-red-800"
          >
            First name is required
          </mat-error>
        }
      </div>

      <div class="min-w-0 flex-1">
        <mat-form-field appearance="fill" class="w-full">
          <mat-label>Last Name</mat-label>
          <input
            matInput
            formControlName="lastName"
            placeholder="Enter last name"
          />
        </mat-form-field>

        @if (lastName?.invalid && lastName?.touched) {
          <mat-error
            class="-mt-2 rounded-b-md bg-red-100 p-1 text-xs text-red-800"
          >
            Last name is required
          </mat-error>
        }
      </div>
    </div>

    <div class="flex w-full flex-row">
      <mat-form-field appearance="fill" class="w-full">
        <mat-label>Email</mat-label>
        <input
          matInput
          formControlName="email"
          placeholder="Enter company email"
          type="email"
        />
        <mat-hint
          >No public email domains (Gmail, Yahoo, Outlook, etc.).</mat-hint
        >
      </mat-form-field>
    </div>

    @if (
      email?.touched &&
      (email?.hasError('required') || email?.hasError('email'))
    ) {
      <mat-error
        class="-mt-2 block w-full rounded-b-md bg-red-100 p-1 text-xs text-red-800"
      >
        A valid email is required
      </mat-error>
    } @else if (email?.touched && email?.hasError('publicEmailDomain')) {
      <mat-error
        class="-mt-2 block w-full rounded-b-md bg-red-100 p-1 text-xs text-red-800"
      >
        Please use a company email address (no public domains).
      </mat-error>
    }

    <!-- Username: visible to everyone in edit mode, visible only to internal users in invite mode -->
    @if (isEditMode || isInternalOnly()) {
      <div class="flex w-full">
        <mat-form-field appearance="fill" class="w-full">
          <mat-label>
            Username
            @if (!isEditMode && isInternalOnly()) {
              <span class="text-cobalt-600 ml-1 text-xs font-medium">
                (Editable - Internal Only)
              </span>
            } @else if (isEditMode) {
              <span class="ml-1 text-xs text-gray-500">(Read-only)</span>
            }
          </mat-label>

          <input
            matInput
            formControlName="username"
            placeholder="Enter username"
            type="email"
            [disabled]="!isInternalOnly()"
          />
        </mat-form-field>
      </div>

      @if (
        !isEditMode &&
        ((username?.hasError('required') && username?.touched) ||
          (username?.hasError('email') && username?.touched))
      ) {
        <div class="flex w-full">
          <mat-error
            class="-mt-2 block w-full rounded-b-md bg-red-100 p-1 text-xs text-red-800"
          >
            @if (username?.hasError('required')) {
              Username is required
            } @else if (username?.hasError('email')) {
              Username must be a valid email
            }
          </mat-error>
        </div>
      }
    }

    <div class="flex w-full">
      <mat-form-field appearance="fill" class="w-full">
        <mat-label>Role</mat-label>
        <mat-select
          formControlName="portalRole"
          placeholder="Select user role"
          [compareWith]="comparePortalRole"
        >
          @for (portalRole of portalRoles$ | async; track portalRole.Id) {
            @if (portalRole.Name) {
              <mat-option
                [value]="portalRole"
                [disabled]="
                  portalRole.Name === 'Super Admin' &&
                  (this.dialogData?.TotalSuperAdmins ?? 0) >= 1
                "
              >
                {{ portalRole.Name }}
              </mat-option>
            }
          }
        </mat-select>
      </mat-form-field>
    </div>

    @if (portalRole?.invalid && portalRole?.touched) {
      <mat-error
        class="-mt-2 block w-full rounded-b-md bg-red-100 p-1 text-xs text-red-800"
      >
        Role is required
      </mat-error>
    }

    @if (hasSubmissionError$ | async) {
      <mat-error class="w-full rounded-md bg-red-100 text-sm text-red-800">
        <div class="-mt-4 flex items-center gap-x-1 p-2">
          <mat-icon class="w-10 text-red-600">error</mat-icon>
          <span>{{ errorMessage }}</span>
        </div>
      </mat-error>
    }

    <mat-dialog-actions>
      <div class="flex w-full justify-end gap-2">
        <button
          class="flex items-center rounded-lg bg-gray-100 px-4 py-2 shadow-sm hover:bg-gray-300"
          mat-dialog-close
        >
          Cancel
        </button>

        <button
          class="flex items-center rounded-lg bg-cobalt-500 px-4 py-2 text-white shadow-sm hover:bg-cobalt-400 disabled:cursor-not-allowed disabled:bg-gray-400 disabled:hover:bg-gray-400"
          type="submit"
          [disabled]="
            (isSubmitting$ | async) ||
            userForm.invalid ||
            (isEditMode && userForm.pristine)
          "
          cdkFocusInitial
        >
          {{ isEditMode ? 'Save changes' : 'Send invitation' }}
        </button>
      </div>
    </mat-dialog-actions>
  </form>
</mat-dialog-content>

